import { useEffect, useState } from 'react';
import type { ApiClient, Raumstand } from '../api';

/**
 * Der Hinweis „berechnet auf altem Raumstand".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis v0.49.2 schrieb jedes Modell mit Raumaufnahme ins Raumbuch, auch das
 * RLT-Modell mit eigenen rund 930 Räumen. Gerechnet wurde auf dem jeweils
 * aktiven Satz. Seit dem 07.10.2026 ist der RLT-Satz entfernt, mehrere
 * Rechnungen verweisen aber weiter auf ihn. Ihre Zahlen sind nicht falsch,
 * sie gehören nur zu anderen Räumen — das muss dort stehen, wo man sie liest.
 *
 * Der Server ermittelt den Stand beim Lesen (GET …/raumstand) und schreibt
 * nichts. Kann der Hinweis nicht geladen werden, zeigt die Seite, was sie
 * immer gezeigt hat: Ein fehlender Hinweis ist kein Fehler der Seite.
 */
export function Raumstandhinweis({
  api, projektId, rechnungen,
}: {
  api: ApiClient;
  projektId: string;
  /** Schlüssel der Rechnungen, deren Stand diese Seite zeigt. */
  rechnungen: string[];
}) {
  const [staende, setStaende] = useState<Raumstand[]>([]);

  useEffect(() => {
    let abgebrochen = false;
    api.raumstand(projektId)
      .then((s) => { if (!abgebrochen) setStaende(s); })
      .catch(() => { if (!abgebrochen) setStaende([]); });
    return () => { abgebrochen = true; };
  }, [api, projektId]);

  const alt = staende.filter((s) => s.aufAltemRaumstand && rechnungen.includes(s.rechnung));
  if (alt.length === 0) return null;

  return (
    <div className="hinweis warnung" role="status" style={{ display: 'block', maxWidth: 860 }}>
      <strong>Berechnet auf altem Raumstand.</strong>{' '}
      Die folgenden Ergebnisse verweisen auf Räume, die im Raumbuch inzwischen
      entfernt sind. Sie gelten für diese Räume, nicht für das aktive Raumbuch;
      neu gerechnet ist nichts.
      <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
        {alt.map((s) => (
          <li key={s.rechnung}>
            {s.bezeichnung}
            {s.gerechnetAm ? ` (gerechnet ${datum(s.gerechnetAm)})` : ''}:{' '}
            {s.entfernt.toLocaleString('de-DE')} von {s.raeume.toLocaleString('de-DE')} Räumen
            entfernt{s.entferntSeit ? `, seit ${datum(s.entferntSeit)}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

function datum(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}
